// Types for the Excel Knowledge Bases admin screen
// (change: excel-knowledge-bases, Slice S4b).
//
// Mirrors CORE-API `modules/kb/dtos/schemas.py` (KbResponse/KbListResponse/
// KbUpdateRequest/KbConfigResponse). Upload is multipart — built as FormData
// in `useCreateKb`, not sent as a JSON body.

export type KbOwnerType = 'user' | 'system'

export type KbStatus = 'uploaded' | 'parsing' | 'indexing' | 'ready' | 'error'

/** Statuses that mean the pipeline is still running — used to keep polling. */
export const KB_ACTIVE_STATUSES: KbStatus[] = ['uploaded', 'parsing', 'indexing']

export interface KnowledgeBase {
  kb_id: string
  owner_type: KbOwnerType
  user_id?: string | null
  name: string
  description?: string | null
  keywords: string[]
  status: KbStatus
  error?: string | null
  chunk_count: number
  sheets?: Array<Record<string, unknown>> | null
  original_filename: string
  size_bytes: number
  created_at?: string | null
  updated_at?: string | null
}

export interface KbListResponse {
  items: KnowledgeBase[]
  total: number
}

export interface KbUpdateRequest {
  name?: string
  description?: string
  keywords?: string[]
}

export interface KbUploadRequest {
  file: File
  name: string
  description?: string
  keywords?: string[]
}

export interface KbLimits {
  max_file_mb: number
  max_rows: number
  max_per_user: number
}

export interface KbConfig {
  enabled: boolean
  can_upload: boolean
  limits: KbLimits
  allowed_plans: string[]
}

// Hard code ceilings mirrored from CORE-API `modules/kb/kb_limits.py` (design
// D6). Client-side validation only — the backend clamps independently and
// remains the source of truth; these constants must stay in sync manually.
export const KB_CEILING_FILE_MB = 50
export const KB_CEILING_ROWS = 200_000
export const KB_CEILING_PER_USER = 20

/** `kb.*` system_config keys, rendered by the dedicated KbLimitsConfig card
 * on the Settings page — excluded from the generic config list there to
 * avoid rendering them twice (design D6: existing GET/PATCH /admin/config,
 * no new admin endpoint). */
export const KB_CONFIG_KEYS = [
  'kb.max_file_mb',
  'kb.max_rows',
  'kb.max_per_user',
  'kb.allowed_plans',
]
